import React, { useState } from "react";
import {
  Box, TextField, IconButton, Tooltip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import { Maximize4 as ExpandIconIx } from "iconsax-react";
import CloseIcon from "@mui/icons-material/Close";
import { LabeledField, inboxInputSx } from "./LabeledField";
import { slimScrollbarSx } from "../../../../../utils/scrollbarStyles";

// Shared "big paragraph" text box — built once for the Instructions step's
// Context field, then rolled out to every genuine long-text box across the
// AI Agents pages (Rahim, 2026-09-04: "now do it for all the paragraph
// text boxes... all around the ai agents pages"). Two real bugs this fixes
// everywhere it's used, not just Context:
//   1. A plain multiline TextField with no `maxRows` grows forever with
//      the content instead of scrolling internally — capped here at
//      `maxRows` with this app's own slim scrollbar formula.
//   2. An expand-to-fullscreen affordance for writing/reading long text
//      comfortably (same pattern ChatGPT/Claude/GitHub/Notion use) — a
//      bordered square button floating in the field's own top-right
//      corner, opening a large modal editor with a bigger font.
//
// The fullscreen editor is a plain native `<textarea>` (Box
// component="textarea"), NOT a MUI TextField — confirmed via reading MUI's
// own InputBase/FormControl source that TextField's outer wrapper div
// never shrinks below its own content's natural height (default flex
// min-height:auto) no matter what's set on the textarea inside it, so it
// silently defeats any fixed-height/internal-scroll layout. A raw textarea
// has none of that wrapper-chain indirection.
//
// Draft/Save/Cancel (Rahim: "there is no save button... when it expands")
// — the fullscreen editor stages its own local draft, seeded from the real
// value on open; Cancel discards it, Save commits via `onChange`. The
// small inline field still saves live on every keystroke, same as every
// other field on these pages — only the fullscreen editor needed the
// staged-draft treatment, since accidentally losing a big rewrite to a
// stray Escape/backdrop click is a much bigger deal there.
export default function ExpandableParagraphField({
  label, value, onChange, placeholder, minRows = 4, maxRows = 14, dialogTitle, sx,
}) {
  const [expandOpen, setExpandOpen] = useState(false);
  const [draft, setDraft] = useState("");

  const openExpand = () => {
    setDraft(value || "");
    setExpandOpen(true);
  };
  const saveExpand = () => {
    onChange(draft);
    setExpandOpen(false);
  };

  return (
    <>
      <LabeledField label={label} sx={sx}>
        {/* position:relative — the expand button floats inside the
            textarea's own top-right corner, not the label row above it
            (Rahim: "shouldnt it be inside the textbox?"). */}
        <Box sx={{ position: "relative" }}>
          <TextField
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            fullWidth size="small" placeholder={placeholder}
            multiline minRows={minRows} maxRows={maxRows}
            sx={{
              ...inboxInputSx,
              // pr clears the overlaid expand button so typed text never
              // runs underneath it.
              "& .MuiInputBase-inputMultiline": { ...slimScrollbarSx, overflowY: "auto", pr: 4.5 },
            }}
          />
          <Tooltip title="Expand">
            <IconButton
              size="small"
              onClick={openExpand}
              aria-label="Expand"
              sx={{
                // right:26 — the slim scrollbar (6px) doesn't sit flush
                // with the field's outer border; it's inset further by the
                // outlined input's own default horizontal padding on top
                // of the border, so anything less than ~26px still lands
                // on top of the scrollbar's real strip (confirmed live).
                position: "absolute", top: 8, right: 26,
                width: 26, height: 26, borderRadius: "6px",
                border: "1px solid", borderColor: "divider",
                bgcolor: (theme) => theme.palette.background.paper,
                color: "text.secondary",
                "&:hover": { bgcolor: (theme) => theme.palette.action.hover, color: "text.primary" },
              }}
            >
              <Box sx={{ width: 14, height: 14, display: "flex" }}>
                <ExpandIconIx size="100%" color="currentColor" variant="Linear" />
              </Box>
            </IconButton>
          </Tooltip>
        </Box>
      </LabeledField>

      <Dialog
        open={expandOpen}
        onClose={() => setExpandOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "80vh", borderRadius: "16px", display: "flex", flexDirection: "column" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1rem", fontWeight: 600, color: "text.primary", borderBottom: "1px solid", borderColor: "divider", px: 3, py: 2.25, flexShrink: 0 }}>
          {dialogTitle || label}
          <IconButton size="small" onClick={() => setExpandOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 0, pb: 3, display: "flex", flex: 1, minHeight: 0 }}>
          <Box
            component="textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            autoFocus
            sx={{
              mt: 3, flex: 1, minHeight: 0, width: "100%",
              resize: "none",
              fontFamily: "inherit",
              fontSize: "1rem",
              lineHeight: 1.6,
              color: (theme) => theme.palette.text.primary,
              bgcolor: "transparent",
              border: "1px solid",
              borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.23)" : theme.palette.divider),
              borderRadius: "8px",
              p: 1.75,
              boxSizing: "border-box",
              outline: "none",
              "&:hover": { borderColor: (theme) => theme.palette.text.secondary },
              "&:focus": { borderColor: (theme) => theme.palette.text.secondary },
              "&::placeholder": { color: (theme) => theme.palette.text.disabled, opacity: 1 },
              overflowY: "auto",
              ...slimScrollbarSx,
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider", justifyContent: "space-between", flexShrink: 0 }}>
          <Button
            onClick={() => setExpandOpen(false)}
            variant="text"
            sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8125rem", color: (theme) => theme.palette.text.secondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={saveExpand}
            variant="outlined"
            sx={{
              borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.8125rem",
              backgroundColor: (theme) => theme.palette.background.default,
              borderColor: (theme) => theme.palette.divider,
              color: (theme) => theme.palette.text.primary,
              "&:hover": { backgroundColor: (theme) => theme.palette.action.hover, borderColor: (theme) => theme.palette.divider },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
